import { useState, useContext } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash3.svg";
import { ReactComponent as PlusSquareFill } from "bootstrap-icons/icons/plus-square-fill.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as CashStack } from "bootstrap-icons/icons/cash-stack.svg";
import { ReactComponent as CardList } from "bootstrap-icons/icons/card-list.svg";
import { ReactComponent as CardHeading } from "bootstrap-icons/icons/card-heading.svg";
import { ReactComponent as List } from "bootstrap-icons/icons/list.svg";
import { ReactComponent as Download } from "bootstrap-icons/icons/download.svg";
import { ReactComponent as Search } from "bootstrap-icons/icons/search.svg";
import { ReactComponent as ArrowClockwise } from "bootstrap-icons/icons/arrow-clockwise.svg";
import { ReactComponent as XCircle } from "bootstrap-icons/icons/x-circle.svg";
import Spinner from "../../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import CustomStyles from "../../../components/utils/CustomStyles";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";
import { AuthContext } from "../../../AuthProvider/AuthProvider";
import formatDate from "../../../components/utils/DateFormatter";
import { downloadCSVFromApi } from "../../../components/utils/DownlaodCSVFromApi";
import { useAllRemittances, useBranches } from "../../../hooks/useAppData";
import ApiServices from "../../../api/ApiServices";
import { handleError } from "../../../components/utils/AuthenticationCheck";

const GetAllRemittance = () => {
    const [loadingCSV, setLoadingCSV] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [confirmationLoading, setConfirmationLoading] = useState(false);
    const { permissions } = useContext(AuthContext);
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [clearFilterLoading, setClearFilterLoading] = useState(false);

    const [filters, setFilters] = useState({
        per_page: 20,
        page: 1,
        branch_id: "",
        status: "",
        type: "",
        start_date: "",
        end_date: "",
    });

    const navigate = useNavigate();

    const { data: branchResponse } = useBranches();
    const branchOpe = (branchResponse?.data || []).slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const { data: remittanceResponse, isLoading: loading } = useAllRemittances(filters);
    const remittance = remittanceResponse?.data?.remittances || remittanceResponse?.data || [];
    const pagination = {
        total: remittanceResponse?.data?.total || 0,
        currentPage: remittanceResponse?.data?.current_page || 1,
        perPage: remittanceResponse?.data?.per_page || 20,
    };

    const isAnyFilterFilled = () => {
        return Object.values(filters).some(
            (value) => value !== "" && value !== null
        );
    };


    const handleClearFilters = () => {
        setFilters({ per_page: 20, page: 1, branch_id: "", status: "", type: "", start_date: "", end_date: "" });
        setClearFilterLoading(false);
    };

    const handleFilterClick = () => {
        if (!isAnyFilterFilled()) return;
    };

    const handlePageChange = (page) => setFilters((prev) => ({ ...prev, page }));
    const handlePerRowsChange = (newPerPage, page) => setFilters((prev) => ({ ...prev, per_page: newPerPage, page }));


    const ExpandableComponent = ({ data }) => (
        <div className="flex justify-start p-4 relative">

            {permissions.some(permission => permission.name === 'branch.remittance.view') && (
                <div className="mx-2 my-2 group relative">
                    <Link
                        to="/operations/branch/remittance/details"
                        state={{ data }}
                    >
                        <Eye className="w-5 h-5 text-dash4 " />
                    </Link>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block  text-darkGreen3 text-xs px-2 py-1 rounded">
                        Details
                    </div>
                </div>
            )}
        </div>
    );


    const columns = [
        {
            name: "Remittance No",
            selector: (row) => row.remittance_number ? row.remittance_number : "NOT AVAILABLE",
            sortable: true,
            width: "220px",
        },
        {
            name: "Branch",
            selector: (row) => row.branch?.name ? row.branch?.name.toUpperCase() : "NOT AVAILABLE",
            sortable: true,
            width: "200px",
        },
        {
            name: "Type",
            selector: (row) => row.type_label ? row.type_label.toUpperCase() : "NOT AVAILABLE",
            sortable: true,
            width: "200px",
        },
        {
            name: "Amount",
            selector: (row) => CurrencyFormatter(row.amount) ? row.amount : "0:00",
            sortable: true,
            width: "160px",
        },
        {
            name: "Status",
            selector: (row) => row.status ? row.status : "NOT AVAILABLE",
            sortable: true,
            width: "160px",
            cell: (row) => (
                <span
                    className={
                        row.status === "pending"
                            ? "text-orange1"
                            : row.status === "completed"
                                ? "text-green-600"
                                : row.status === "rejected"
                                    ? "text-red-600"
                                    : "text-darkGreen3"
                    }
                >
                    {row.status.toUpperCase()}
                </span>
            ),
        },
        {
            name: "Description",
            selector: (row) => row.description ? row.description.toUpperCase() : "NOT AVAILABLE",
            wrap: true,
            width: "300px",
        },
        {
            name: "Date Submitted",
            selector: (row) => formatDate(row.submitted_at) ? formatDate(row.submitted_at) : "NOT AVAILABLE",
            width: "200px",
        },
    ];


    // Check if both dates are selected
    const areDatesSelected = filters.start_date && filters.end_date;

    // Filter data based on search text, AND date range
    const filteredItems = remittance.filter((item) => {
        // Filter by search text
        const matchesFilterText =
            item.remittance_number?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.branch?.name.toLowerCase().includes(filterText.toLowerCase()) ||
            item.type_label?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.closing_stock?.toString().includes(filterText) ||
            item.amount?.includes(filterText.toLowerCase()) ||
            item.description?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.initiator?.fullname.toLowerCase().includes(filterText.toLowerCase()) ||
            item.approver?.fullname.toLowerCase().includes(filterText.toLowerCase()) ||
            item.status.toLowerCase().includes(filterText.toLowerCase());


        // Filter by date range (if dates are selected)
        let matchesDateRange = true;
        if (areDatesSelected) {
            // Use snapshot_date instead of created_at for stock monitoring
            const itemDate = new Date(item.snapshot_date || item.created_at);
            const startDate = new Date(filters.start_date);
            const endDate = new Date(filters.end_date);

            // Set time to end of day for end date
            endDate.setHours(23, 59, 59, 999);

            matchesDateRange = itemDate >= startDate && itemDate <= endDate;
        }

        return matchesFilterText && matchesDateRange;
    });


    // Handle CSV Download - Server-side filtered by date
    const handleDownloadCSV = async () => {
        if (!areDatesSelected) {
            toast.error("Please select both start and end dates to download CSV");
            return;
        }

        // Validate date range
        const startDate = new Date(filters.start_date);
        const endDate = new Date(filters.end_date);

        if (startDate > endDate) {
            toast.error("Start date must be before end date");
            return;
        }

        // limit date range to a year
        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
        if (daysDiff > 365) {
            toast.error("Date range cannot exceed 1 year");
            return;
        }

        setLoadingCSV(true);

        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;

            if (!token) {
                toast.error("No access token found");
                return;
            }

            // Prepare download payload
            const downloadPayload = {
                start_date: filters.start_date,
                end_date: filters.end_date,
                download: 1,
            };
            // console.log("Download payload:", downloadPayload);

            // Create filename with date range
            const startDateFormatted = filters.start_date.replace(/-/g, '_');
            const endDateFormatted = filters.end_date.replace(/-/g, '_');
            const filename = `all_remittance_${startDateFormatted}_to_${endDateFormatted}.csv`;

            // Call the download function
            await downloadCSVFromApi(
                ApiServices.getAllRemittance,
                downloadPayload,
                token,
                {
                    filename: filename,
                    columnsToRemove: [], // Add sensitive columns to exclude 
                    maxRows: 50000
                }
            );

        } catch (error) {
            // console.error("Download error:", error);
            handleError(error, navigate);
            // Additional download-specific error messages
            if (error.message && error.message.includes("CSV")) {
                toast.error("Failed to process CSV file");
            } else if (error.message && error.message.includes("network")) {
                toast.error("Network error during download");
            }
        } finally {
            setLoadingCSV(false);
        }
    };

    // Clear date filters
    const handleClearDates = () => {
        setFilters({
            start_date: "",
            end_date: "",
        });
        toast.success("Date filters cleared");
    };


    //filter data by name
    const handleFilter = (e) => {
        setFilterText(e.target.value);
        setResetPaginationToggle(!resetPaginationToggle);
    };

    const data = filteredItems.map((remittance) => ({
        remittance_number: remittance.remittance_number || "NOT AVAILABLE",
        reference: remittance.reference || "NOT AVAILABLE",
        branch: remittance.branch?.name || "NOT AVAILABLE",
        type: remittance.type_label || "NOT AVAILABLE",
        amount: remittance.amount || 0,
        status: remittance.status || "NOT AVAILABLE",
        description: remittance.description || "NOT AVAILABLE",
        submitted_at: remittance.submitted_at || "NOT AVAILABLE",
        approved_at: remittance.approved_at || "NOT AVAILABLE",
        completed_at: remittance.completed_at || "NOT AVAILABLE",
        created_at: remittance.created_at || "NOT AVAILABLE",
        initiator: remittance.initiator?.fullname || "NOT AVAILABLE",
        approver: remittance.approver?.fullname || "NOT AVAILABLE",
    }));


    const sortedData = [...data].sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA;
    });

    return (
        <MainLayouts>
            <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
                <section
                    className="w-full pr-[3%] pt-3 my-3"
                    style={{ backgroundColor: "#F7F7F7" }}
                >
                    <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                        <ol className="list-none p-0 inline-flex mb-2">
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                    Operations
                                </p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                    Remittance
                                </p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-orange1">List</p>
                            </li>
                        </ol>
                    </nav>
                </section>


                {/* i dey here  */}
                {/* Filter Controls Section - Responsive */}
                <div className="pb-4 bg-white dark:bg-gray-900">
                    {/* DESKTOP FILTERS (md and up) - All in one row */}
                    <div className="hidden md:flex flex-wrap items-center gap-3">
                        {/* Search Input Field with Icon */}
                        <div className="relative flex-grow min-w-[200px]">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="search"
                                    className="block w-full pl-10 pr-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                    placeholder="Search ..."
                                    value={filterText}
                                    onChange={handleFilter}
                                />
                            </div>
                        </div>


                        {/* Clear Dates Button - Only show when dates are selected */}
                        {areDatesSelected && (
                            <div className="min-w-[120px]">
                                <button
                                    onClick={handleClearDates}
                                    className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                                >
                                    <XCircle className="w-4 h-4" />
                                    Clear Dates
                                </button>
                            </div>
                        )}

                        {/* Download Button */}
                        <div className="min-w-[160px]">
                            <button
                                onClick={handleDownloadCSV}
                                className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${areDatesSelected
                                    ? "bg-orange1 text-lGrey3 hover:bg-orange-600 cursor-pointer"
                                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    }`}
                                disabled={!areDatesSelected || loadingCSV}
                                title={!areDatesSelected ? "Select start and end dates to enable download" : ""}
                            >
                                {loadingCSV ? (
                                    <>
                                        <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <Download className="w-4 h-4" />
                                        Download CSV
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* MOBILE FILTERS (below md) - Stacked layout */}
                    <div className="md:hidden flex flex-col gap-3">
                        {/* Search - Full width on mobile */}
                        <div className="w-full">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="search"
                                    className="block w-full pl-10 pr-3 py-3 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
                                    placeholder="Search customers..."
                                    value={filterText}
                                    onChange={handleFilter}
                                />
                            </div>
                        </div>

                        {/* Filters grid for mobile */}
                        <div className="grid grid-cols-2 gap-3">

                            {/* Clear Dates Button - Full width when shown */}
                            {areDatesSelected && (
                                <div className="col-span-2">
                                    <button
                                        onClick={handleClearDates}
                                        className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-3 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800"
                                    >
                                        <XCircle className="w-4 h-4" />
                                        Clear Dates
                                    </button>
                                </div>
                            )}

                            {/* Download Button - Full width on mobile */}
                            <div className="col-span-2">
                                <button
                                    onClick={handleDownloadCSV}
                                    className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-3 text-sm font-semibold ${areDatesSelected
                                        ? "bg-orange1 text-lGrey3 hover:bg-orange-600"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        }`}
                                    disabled={!areDatesSelected || loadingCSV}
                                >
                                    {loadingCSV ? (
                                        <>
                                            <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                                            <span className="ml-2">Downloading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4" />
                                            <span className="ml-2">Download CSV</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* DESKTOP help text (md and up) */}
                    <div className="hidden md:flex items-center justify-between mt-2">
                        {!areDatesSelected && (
                            <p className="text-xs text-gray-500">
                                Select start and end dates to enable download
                            </p>
                        )}
                        {areDatesSelected && sortedData.length > 0 && (
                            <p className="text-sm text-gray-600">
                                Showing {sortedData.length} records from {filters.start_date} to {filters.end_date}
                            </p>
                        )}
                    </div>

                    {/* MOBILE help text (below md) */}
                    <div className="md:hidden mt-3">
                        {!areDatesSelected && (
                            <p className="text-xs text-gray-500 text-center mb-2">
                                Select start and end dates to enable download
                            </p>
                        )}
                        {areDatesSelected && sortedData.length > 0 && (
                            <p className="text-sm text-gray-600 text-center">
                                Showing {sortedData.length} records
                            </p>
                        )}
                    </div>
                </div>
                {/* i dey here */}

                <div className="pb-4 bg-white dark:bg-gray-900">
                    <div
                        className="
            grid grid-cols-1 gap-3
            sm:flex sm:flex-wrap sm:items-center sm:gap-3
        "
                    >
                        {/* Branch */}
                        <select
                            className="border rounded px-3 py-2 border-lGrey w-full sm:w-[200px]"
                            value={filters.branch_id}
                            onChange={(e) =>
                                setFilters({ ...filters, branch_id: e.target.value })
                            }
                        >
                            <option value="">All Branches</option>
                            {branchOpe.map((branch) => (
                                <option key={branch.id} value={branch.id}>
                                    {branch.name}
                                </option>
                            ))}
                        </select>

                        {/* Status */}
                        {/* <select
                            className="border rounded px-3 py-2 border-lGrey w-full sm:w-[200px]"
                            value={filters.status}
                            onChange={(e) =>
                                setFilters({ ...filters, status: e.target.value })
                            }
                        >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                            <option value="rejected">Rejected</option>
                        </select> */}

                        {/* Type */}
                        {/* <select
                            className="border rounded px-3 py-2 border-lGrey w-full sm:w-[220px]"
                            value={filters.type}
                            onChange={(e) =>
                                setFilters({ ...filters, type: e.target.value })
                            }
                        >
                            <option value="">All Types</option>
                            <option value="branch_initiated">Branch Remittance</option>
                            <option value="admin_deduction">Admin Deduction</option>
                        </select> */}

                        {/* Start Date */}
                        <input
                            type="date"
                            className="border rounded px-3 py-2 border-lGrey w-full sm:w-[200px]"
                            value={filters.start_date}
                            onChange={(e) =>
                                setFilters({ ...filters, start_date: e.target.value })
                            }
                        />

                        {/* End Date */}
                        <input
                            type="date"
                            className="border rounded px-3 py-2 border-lGrey w-full sm:w-[200px]"
                            value={filters.end_date}
                            onChange={(e) =>
                                setFilters({ ...filters, end_date: e.target.value })
                            }
                        />

                        {/* Buttons */}
                        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                            <button
                                onClick={handleFilterClick}
                                className="
                    bg-darkGreen3 text-lGrey px-4 py-2 rounded
                    disabled:opacity-50
                    w-full sm:w-auto
                "
                                disabled={!isAnyFilterFilled() || filterLoading}
                            >
                                {filterLoading ? "Filtering..." : "Filter"}
                            </button>

                            {isAnyFilterFilled() && (
                                <button
                                    onClick={handleClearFilters}
                                    className="
                        bg-orange1 text-lGrey px-4 py-2 rounded
                        hover:bg-orange1 transition-colors
                        w-full sm:w-auto
                    "
                                    disabled={clearFilterLoading}
                                >
                                    {clearFilterLoading ? "Clearing..." : "Clear Filters"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <Spinner />
                ) : (
                    <DataTable
                        columns={columns}
                            data={filteredItems}
                        pagination
                        highlightOnHover
                        striped
                        customStyles={CustomStyles}
                        noDataComponent={<div>No remittance found</div>}
                        expandableRows
                        expandableRowsComponent={(props) => (
                            <ExpandableComponent data={props.data} />
                        )}
                    />
                )}
            </div>
        </MainLayouts>
    );
};

export default GetAllRemittance;
